import React, {Suspense, useEffect, useState} from 'react';
import {Routes, Route, Navigate, useLocation, matchPath} from "react-router-dom";
import {useAppDispatch, useAppSelector} from "../hooks/redux";
import {fetchCheckAuth, fetchLogin} from "../features/auth/model/actions";
import {fetchGetAllUsers} from "../features/users/model/actions";
import {CssBaseline, Stack, ThemeProvider, Box as MuiBox, useMediaQuery} from "@mui/material";
import Box from "@mui/material/Box";
import {IRouteConfig, routesConfig} from "../config/routes";
import SideMenu from "./layout/sideBar/SideMenu";
import AppNavbar from "./layout/AppNavbar";
import {theme} from "../styles/theme/theme";
import MessageWindow from "../features/messages/ui/MessageWindow";
import Message from "../features/messages/ui/Message";
import {selectIsAuth} from "../features/auth/model/selectors";
import CircularProgress from "@mui/material/CircularProgress";
import {routes} from "../utils/routes";
import {selectCompanyTariffName} from "../features/companies/model/selectors";
import {selectCurrentUser} from "../features/users/model/selectors";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {ruRU} from "@mui/x-date-pickers/locales";
import {MobileHeaderActionsProvider} from "../context/MobileHeaderActionsContext";
import ConsentReacceptModal from "../features/legal/ui/ConsentReacceptModal";
import CookieConsentBanner from "../features/legal/ui/CookieConsentBanner";
import {t} from '../styles/theme/tokens';
import {MOBILE_NAVBAR_HEIGHT} from "./layout/layout.constants";
import {useIsAdmin} from "../hooks/useIsAdmin"; // ← путь поправь под свой
import {DEMO_GUEST_EMAIL, DEMO_GUEST_PASSWORD, isDemoMode} from "../config/demo";
import ErrorBoundary from "./common/ErrorBoundary";
import {warmPmtiles} from "../features/map_history_gl/config/pmtilesWarm";
import DangerTitleAlert from "../features/map/ui/DangerTitleAlert";

const PageFallback = () => (
    <MuiBox sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1}}>
        <CircularProgress/>
    </MuiBox>
);

function App() {
    const dispatch = useAppDispatch();
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);
    const isAuth = useAppSelector(selectIsAuth);
    const isAdmin = useIsAdmin();
    // Ролевой гард роутов (requiresRoles): текущий пользователь из стора.
    const currentUser = useAppSelector(selectCurrentUser);                                    // ← роль для гарда
    const companyTariffName = useAppSelector(selectCompanyTariffName);
    const isNavbarVisible = useMediaQuery(theme.breakpoints.down("md"));
    const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
    const location = useLocation();
    const isMapRoute = location.pathname.startsWith('/map');

    useEffect(() => {
        // Демо-режим (?demo=1): принудительно входим гостём. Перетираем любой
        // чужой токен, который мог остаться на этом домене от обычного юзера —
        // демо это отдельный read-only режим. Эффект отрабатывает один раз
        // (deps: [dispatch]), fetchLogin одноразовый → зацикливания нет.
        if (isDemoMode()) {
            localStorage.removeItem("access_token");
            dispatch(fetchLogin({email: DEMO_GUEST_EMAIL, password: DEMO_GUEST_PASSWORD}))
                .finally(() => setIsCheckingAuth(false));
            return;
        }
        if (!localStorage.getItem("access_token")) {
            setIsCheckingAuth(false);
            return;
        }
        dispatch(fetchCheckAuth()).finally(() => setIsCheckingAuth(false));
    }, [dispatch]);

    useEffect(() => {
        if (isAuth) dispatch(fetchGetAllUsers());
    }, [isAuth, dispatch]);

    // Прогрев карты сразу после входа (не дожидаясь маршрута карты): чанк
    // maplibre-gl + пул его воркеров, заголовок+корневая директория PMTiles,
    // спрайт и глифы — всё, что лежит на критическом пути первого кадра.
    // Идемпотентно (работа выполняется один раз за сессию), не блокирует
    // рендер, ошибки глотает сам модуль.
    useEffect(() => {
        if (isAuth) warmPmtiles();
    }, [isAuth]);

    const getAllRoutes = (routesList: IRouteConfig[]): IRouteConfig[] => {
        const result: IRouteConfig[] = [];
        routesList.forEach(route => {
            result.push(route);
            if (route.children) result.push(...getAllRoutes(route.children));
        });
        return result;
    };

    const allRoutes = getAllRoutes(routesConfig);
    // standalone исключены из обеих обычных веток: они рендерятся отдельно
    // ниже и не должны попадать внутрь оболочки приложения.
    const standaloneRoutes = allRoutes.filter(r => r.standalone);
    const publicRoutes = allRoutes.filter(r => !r.requiresAuth && !r.standalone);
    const privateRoutes = allRoutes.filter(r => r.requiresAuth);

    /**
     * Публичные страницы по токену (гостевая группа, отслеживание рейса)
     * рендерятся ДО app-shell и ДО проверки авторизации.
     *
     * Почему до, а не «просто в публичной ветке»: publicRoutes раньше
     * подмешивались И в авторизованную ветку тоже. У посетителя с живым
     * access_token в этой вкладке (владелец, открывший свою же ссылку, или
     * защёлкнутая демо-сессия с лендинга) гостевая страница монтировалась
     * внутрь SideMenu/AppNavbar — и гость получал навигацию по чужому
     * кабинету. В dev этого не воспроизводилось: там вкладка была чистая,
     * isAuth === false, и отрабатывала публичная ветка.
     *
     * Ранний выход делает утечку невозможной структурно: до оболочки
     * исполнение просто не доходит. Ждать isCheckingAuth тоже незачем —
     * гостю авторизация не нужна, а лишний спиннер задерживал бы страницу.
     */
    const isStandaloneRoute = standaloneRoutes.some(
        (r) => matchPath(r.path, location.pathname) !== null,
    );

    if (isStandaloneRoute) {
        return (
            <ThemeProvider theme={theme} defaultMode="dark">
                <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="ru"
                    localeText={ruRU.components.MuiLocalizationProvider.defaultProps.localeText}
                >
                    <CssBaseline/>
                    <Suspense fallback={<PageFallback/>}>
                        <Routes>
                            {standaloneRoutes.map(route => (
                                <Route key={route.path} path={route.path} element={route.element}/>
                            ))}
                        </Routes>
                    </Suspense>
                </LocalizationProvider>
            </ThemeProvider>
        );
    }

    if (isCheckingAuth) {
        return (
            <ThemeProvider theme={theme} defaultMode="dark">
                <CssBaseline/>
                <MuiBox sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100dvh'}}>
                    <CircularProgress/>
                </MuiBox>
            </ThemeProvider>
        );
    }

    if (!isAuth) {
        return (
            <ThemeProvider theme={theme} defaultMode="dark">
                {/* LocalizationProvider нужен и здесь: публичные страницы тоже
                    показывают MyDatePicker (гостевой отчёт по группе), а без
                    контекста пикер падает на этапе рендера. Раньше провайдер
                    стоял только в авторизованной ветке. */}
                <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="ru"
                    localeText={ruRU.components.MuiLocalizationProvider.defaultProps.localeText}
                >
                    <CssBaseline/>
                    <Suspense fallback={<PageFallback/>}>
                        <Routes>
                            {publicRoutes.map(route => (
                                <Route key={route.path} path={route.path} element={route.element}/>
                            ))}
                            {/* Гость на любой неизвестный/приватный путь → форма входа */}
                            <Route path="*" element={<Navigate to={routes.login}/>}/>
                        </Routes>
                    </Suspense>
                    <MessageWindow/>
                    <Message/>
                </LocalizationProvider>
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={theme} defaultMode="dark">
            <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="ru"
                localeText={ruRU.components.MuiLocalizationProvider.defaultProps.localeText}
            >
                <MobileHeaderActionsProvider>
                    <CssBaseline/>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        height: '100dvh',
                        overflow: 'hidden',
                    }}>
                        <SideMenu/>
                        <AppNavbar/>
                        <Box
                            component="main"
                            sx={{
                                flexGrow: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                                backgroundColor: t.bg.default,
                            }}
                        >
                            <Stack
                                sx={{
                                    flexGrow: 1,
                                    minHeight: 0,
                                    height: isMapRoute ? '100%' : 'auto',
                                    overflow: isMapRoute ? 'hidden' : 'auto',
                                    px: (isPhone && isMapRoute) ? 0 : (isPhone ? 1 : 2),
                                    pb: (isPhone && isMapRoute) ? 0 : (isPhone ? 1 : 2),
                                    pt: (isNavbarVisible && !isMapRoute) ? `${MOBILE_NAVBAR_HEIGHT + 16}px` : 0,
                                    boxSizing: 'border-box',
                                    scrollbarGutter: isMapRoute ? 'auto' : 'stable',
                                }}
                                spacing={0}
                            >
                                {/* Страничный ErrorBoundary: падение контента страницы
                                    не сносит layout (SideMenu/AppNavbar остаются живыми).
                                    key={pathname} пересоздаёт бордер при смене роута, чтобы
                                    навигация по меню сбрасывала hasError и восстанавливала
                                    контент без полного reload. */}
                                <ErrorBoundary key={location.pathname}>
                                    <Suspense fallback={<PageFallback/>}>
                                        <Routes>
                                            {privateRoutes.map(route => {
                                                // Гард по тарифу (как было) + новый гард по роли админа.
                                                const deniedByTariff =
                                                    route.requiresTariff && route.requiresTariff !== companyTariffName;
                                                const deniedByAdmin =
                                                    route.requiresAdmin && !isAdmin;
                                                // Гард по роли: список requiresRoles задан и
                                                // роль текущего пользователя в него не входит.
                                                const deniedByRole =
                                                    route.requiresRoles &&
                                                    !route.requiresRoles.includes(currentUser?.role_id ?? -1);

                                                return (
                                                    <Route
                                                        key={route.path}
                                                        path={route.path}
                                                        element={
                                                            (deniedByTariff || deniedByAdmin || deniedByRole)
                                                                ? <Navigate to={routes.map} replace/>
                                                                : route.element
                                                        }
                                                    />
                                                );
                                            })}
                                            {publicRoutes.map(route => (
                                                <Route key={route.path} path={route.path} element={route.element}/>
                                            ))}
                                            {/* Стартовая для авторизованного: "/" → сводка по автопарку.
                                            Гостя это не затрагивает — у него отдельная ветка выше. */}
                                            <Route path="/" element={<Navigate to={routes.fleetDashboard} replace/>}/>
                                            {/* Авторизованный на неизвестный путь → карта (дом) */}
                                            <Route path="*" element={<Navigate to={routes.map}/>}/>
                                        </Routes>
                                    </Suspense>
                                </ErrorBoundary>
                            </Stack>
                        </Box>
                        <MessageWindow/>
                        <Message/>
                        {/* Тревога в заголовке вкладки: «⛔ (n)» пока есть
                            danger-тревога у машин — видно с фоновой вкладки. */}
                        <DangerTitleAlert/>
                        <ConsentReacceptModal/>
                        <CookieConsentBanner/>
                    </Box>
                </MobileHeaderActionsProvider>
            </LocalizationProvider>
        </ThemeProvider>
    );
}

export default App;